import { Injectable, inject, signal } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn, FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { type ActivatedRoute, type Params, Router } from '@angular/router';
import { type Observable, delay, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { type AppFileType } from '../../modules/user-management/users/user.model';
import { ImagePreviewDialogComponent } from '../components/image-preview-dialog/image-preview-dialog.component';
import { type ColorOption, COLOR_OPTIONS } from '../data/status-color-codes';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  private dialog = inject(MatDialog);
  private router = inject(Router);

  colorOptions: ColorOption[] = COLOR_OPTIONS;

  noWhitespaceValidatorAsync(control: FormControl): Observable<any> {
    const value = control.value;
    const isWhitespace = value !== null && value !== undefined && value.toString().trim().length === 0;
    const isValid = !isWhitespace;
    return of(isValid ? null : { whitespace: true }).pipe(delay(0)); // Example async validator
  }

  emailValidator(): ValidatorFn {
    // Balanced, production-friendly regex
    const emailRegex = /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control.value ?? '').toString().trim();

      if (!value) {
        return null;
      }

      return emailRegex.test(value) ? null : { invalidEmail: true };
    };
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) {
      return '';
    }

    const date = new Date(dateString);

    // Use the browser's locale automatically
    return new Intl.DateTimeFormat(navigator.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }

  formatDateForInput(date: string): Date {
    return new Date(date);
  }

  playSuccessBeep(): void {
    const audio = new Audio('assets/sounds/beep.mp3');
    audio.play().catch(error => console.error('Error playing sound:', error));
  }

  playErrorBeep(): void {
    const audio = new Audio('assets/sounds/error-beep.wav');
    audio.play().catch(error => console.error('Error playing sound:', error));
  }

  removeNullFields(formObject: any): any {
    const formObjectData = JSON.parse(JSON.stringify(formObject));

    const cleanValue = (value: any): any => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
      }

      if (Array.isArray(value)) {
        const cleanedArray = value.map(item => cleanValue(item)).filter(item => item !== undefined);

        return cleanedArray;
      }

      if (value && typeof value === 'object' && !(value instanceof Date)) {
        const cleanedObj: Record<string, any> = {};
        Object.entries(value).forEach(([key, val]) => {
          const cleaned = cleanValue(val);
          if (cleaned !== undefined && (typeof cleaned !== 'object' || cleaned instanceof Date || Object.keys(cleaned).length > 0 || Array.isArray(cleaned))) {
            cleanedObj[key] = cleaned;
          }
        });
        return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined;
      }

      return value !== null && value !== undefined ? value : undefined;
    };

    const cleaned = cleanValue(formObjectData);
    return cleaned || {};
  }

  syncQueryParams(route: ActivatedRoute, queryParams: Params, replaceUrl = true): void {
    this.router.navigate([], {
      relativeTo: route,
      queryParams,
      replaceUrl
    });
  }

  validateAllFormFields(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAllAsTouched();
        control.updateValueAndValidity();
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.validateAllFormFields(control);
      }
    });
  }

  disableFormControls(control: AbstractControl | null): void {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        this.disableFormControls(control.get(key));
      });
    } else if (control instanceof FormArray) {
      const controls = (control as FormArray).controls;
      controls.forEach(formControl => {
        this.disableFormControls(formControl);
      });
    } else if (control instanceof FormControl) {
      control.disable();
      control.updateValueAndValidity();
    }
  }

  resetFormControls(control: AbstractControl | null): void {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        this.resetFormControls(control.get(key));
      });
    } else if (control instanceof FormArray) {
      const controls = (control as FormArray).controls;
      controls.forEach(formControl => {
        this.resetFormControls(formControl);
      });
    } else if (control instanceof FormControl) {
      control.reset();
      control.updateValueAndValidity();
    }
  }

  clear(form: FormGroup | AbstractControl, formControl: string): void {
    const control = form.get(formControl);

    if (control instanceof FormGroup) {
      control.reset();
    } else {
      control?.setValue(null);
      control?.markAsDirty();
      control?.markAsTouched();
    }
  }

  private pageInfoSignal = signal<any>({});
  readonly pageInfo = this.pageInfoSignal.asReadonly();

  updatePageInfo(pageInfo: any): void {
    this.pageInfoSignal.set(pageInfo);
  }

  updateKiIndustryType(industryType: string): void {
    // Third 'LSF' fallback found alongside kiIndustryTypeOrDefault and auth.service.ts's call site -
    // corrected to 'RMBF' for the same reason (TDD 13 §12).
    const normalizedType = industryType.toUpperCase() || 'RMBF';
    localStorage.setItem('kiIndustryType', normalizedType);
  }

  getActiveFilterCount(form: FormGroup): number {
    const values = form.getRawValue();

    let count = 0;

    for (const key in values) {
      if (key === 'active') {
        continue;
      }

      const value = values[key];

      // Handle nested object like country.id
      if (typeof value === 'object' && value !== null) {
        for (const nestedKey in value) {
          const nestedValue = value[nestedKey];
          if (nestedValue !== null && nestedValue !== '') {
            count++;
          }
        }
      } else if (value !== null && value !== '') {
        count++;
      }
    }

    return count;
  }

  checkIfInactive(control: FormControl, list: any[]): Record<string, boolean> {
    const id = control.value;
    if (typeof id === 'string' && id && list.filter(item => item.active).findIndex(item => item.id === id) === -1) {
      // console.log('checkIfInactive: ', id, ' not found in list');
      return { inactive: true };
    }
    return {};
  }

  canWorkOn(dateInput: string | Date | null | undefined, days = 7): boolean {
    if (!dateInput) {
      return false;
    }

    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (!(d instanceof Date) || isNaN(d.getTime())) {
      return false;
    }

    const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const cutoff = new Date(startOfDay);
    cutoff.setDate(cutoff.getDate() + days);

    return new Date() < cutoff;
  }

  updateClientTheme(): void {
    const theme = this.getClientTheme();
    document.body.classList.remove('default_theme', 'blue_theme', 'gold_theme', 'default-dark-theme', 'blue-dark-theme', 'gold-dark-theme');
    document.body.classList.add(theme);
  }

  getClientTheme(): string {
    const colourTheme = localStorage.getItem('colourTheme');
    if (!colourTheme || colourTheme === 'undefined') {
      return 'default_theme';
    }
    return colourTheme;
  }

  openImagePreview(imageUrl: string, imageName: string): void {
    this.dialog.open(ImagePreviewDialogComponent, {
      width: '90%',
      height: '90%',
      maxWidth: '1200px',
      panelClass: 'image-preview-dialog',
      data: { imageUrl, imageName }
    });
  }

  /** Opens preview only when `imageUrl` is a non-empty string. */
  openImagePreviewIfPresent(imageUrl: string | null | undefined, imageName: string): void {
    const u = typeof imageUrl === 'string' ? imageUrl.trim() : '';
    if (u) {
      this.openImagePreview(u, imageName);
    }
  }

  /**
   * Prefer `webURL` when present; otherwise `tempBaseUrl` + `location`.
   * Returns '' when neither yields a usable URL.
   */
  /**
   * The URL for a member's profile photo, or '' when they have none.
   *
   * Members carry their photo in `images` - the same AppFileType gallery
   * erp-base's /loggedInUser/profile has always exposed. There is no longer a
   * `profilePhotoUrl` string on any endpoint (September 2026): it was a computed
   * getter on the backend entity, which a closed projection never fetched, so it
   * came back null on /member/{id}, /chapter/{id}/officers and the directory
   * alike - and it was a second spelling of something the wire already carried.
   *
   * Picks the flagged default thumbnail, else the first image, then resolves it
   * through the same webURL-then-location path the logged-in user's avatar uses.
   */
  memberPhotoUrl(member: { images?: AppFileType[] | null } | null | undefined): string {
    const images = member?.images;
    if (!images?.length) {
      return '';
    }
    const photo = images.find(image => image.defaultThumbnail) ?? images[0];
    return this.resolveFileImageSrc(photo, environment.tempImageUrl);
  }

  /**
   * The URL for an event's poster, or '' when it has none.
   *
   * Events carry their poster in `poster` - a single AppFileType, the same
   * shape a member's photo uses in `images`. There is no longer a `posterUrl`
   * string on any endpoint (September 2026): it was a computed getter on the
   * backend entity that `EventFM` never fetched (a closed projection only
   * queries the properties it declares), so `GET /event/{id}` returned
   * `"posterUrl": null` for every event that had one - the same defect that
   * hid member photos.
   */
  eventPosterUrl(event: { poster?: AppFileType | null } | null | undefined): string {
    return this.resolveFileImageSrc(event?.poster, environment.tempImageUrl);
  }

  /**
   * Formats an amount with the currency it is actually in.
   *
   * Deliberately not Angular's `currency` pipe. That pipe's first line is
   * `currencyCode || this._defaultCurrencyCode`, and `DEFAULT_CURRENCY_CODE` is not
   * provided anywhere in this app — so its default is Angular's own, `'USD'`. Passing
   * an empty or missing code therefore renders **dollars**, which is the one outcome
   * worse than showing no symbol at all. Callers that hardcoded `'INR'` had the
   * mirror-image bug: right answer, wrong reason, and wrong the moment a chapter is
   * ever set to anything else.
   *
   * Takes the symbol from the data, falls back to the code, and formats the number
   * bare when neither is known. Grouping stays `en-IN` (lakh/crore), matching the
   * Flutter `formatMoney` this mirrors — the app is India-only (base TDD §2); what
   * changed is that the *symbol* is no longer an assumption.
   */
  formatMoney(amount: number | null | undefined, currency?: { symbol?: string; code?: string } | null): string {
    if (amount === null || amount === undefined) {
      return '';
    }
    const prefix = (currency?.symbol || currency?.code || '').trim();
    const fractionDigits = Number.isInteger(amount) ? 0 : 2;
    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).format(amount);
    return prefix ? `${prefix}${formatted}` : formatted;
  }

  resolveFileImageSrc(file: { webURL?: unknown; location?: string | null } | null | undefined, tempBaseUrl: string): string {
    if (!file) {
      return '';
    }
    const web = file.webURL;
    if (web !== null && web !== '') {
      if (typeof web === 'string') {
        const t = web.trim();
        if (t) {
          return t;
        }
      }
    }
    const loc = file.location?.trim();
    if (loc) {
      return tempBaseUrl + loc;
    }
    return '';
  }

  getSortParam(...fields: { field: string; direction: 'asc' | 'desc' }[]): string[] {
    return fields.filter(s => s.field).map(s => `${s.field},${s.direction}`);
  }

  preventNegative(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === 'e' || event.key === ' ') {
      event.preventDefault();
    }
  }

  /** Allows digits and one decimal point for any decimal number input. Use as (keydown) handler. */
  allowDecimalInput(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input?.value ?? '';
    if (event.key === '.' && currentValue.includes('.')) {
      event.preventDefault();
      return;
    }
    if (event.key.length === 1 && !/[\d.]/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Restricts the number of decimal places to `decimalPlaces` (0 = integers only).
   * Use as (input) handler alongside allowDecimalInput.
   */
  restrictNumberInput(event: Event, decimalPlaces: number): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    const value = input.value;
    const parts = value.split('.');
    const intPart = parts[0]?.replace(/[^\d]/g, '') ?? '';
    if (decimalPlaces === 0) {
      const newValue = intPart;
      if (newValue !== value) {
        input.value = newValue;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return;
    }
    const decPart = parts[1]?.replace(/[^\d]/g, '') ?? '';
    const truncatedDec = decPart.slice(0, decimalPlaces);
    const hasDecimalPoint = parts.length > 1;
    const newValue = truncatedDec ? `${intPart}.${truncatedDec}` : hasDecimalPoint ? `${intPart}.` : intPart;
    if (newValue !== value) {
      input.value = newValue;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  getStatusClass(active: boolean): string {
    return active ? 'status-active' : 'status-inactive';
  }

  get kiIndustryType(): string {
    return localStorage.getItem('kiIndustryType') || '';
  }

  get kiIndustryTypeOrDefault(): string {
    // 'RMBF' - RMBF is single-tenant (no KiClient concept, base TDD), so this fallback is always what's
    // actually used in practice; kept as a livestock-family-consistent getter name/shape (matches
    // livestock's kiIndustryTypeOrDefault, SGB's kiIndustryTypeOrSGB, Assetotal's kiIndustryTypeOrNDT) but
    // was still returning livestock's own 'LSF' abbreviation here, missed during the earlier rebrand pass
    // (TDD 13 §12). This value is the multi-tenant discriminator on every Translation document's compound
    // unique index (base TDD §6 / TDD 13 §6.1) - RMBF needs its own value, not livestock's.
    return localStorage.getItem('kiIndustryType') || 'RMBF';
  }

  trimLeadingSpacesAndValidate(event: Event, form: FormGroup, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value.replace(/^\s+/, ''); // remove leading spaces only
    const control = form.get(controlName);

    if (!control) {
      return;
    }

    if (!control.touched) {
      control.markAsTouched(); // mark as touched to trigger validation
    }

    const errors = { ...(control.errors || {}) };

    // Ensure at least 2 valid characters (excluding leading spaces)
    if (currentValue.trim().length < 2) {
      errors['minlength'] = true;
    } else {
      delete errors['minlength'];
    }

    control.setValue(currentValue, { emitEvent: false });
    control.setErrors(Object.keys(errors).length ? errors : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  getDueDateClass(event: any): string {
    if (!event.expiryDate) {
      return '';
    }

    const now = new Date();
    const dueDate = new Date(event.expiryDate);

    if (now > dueDate) {
      return 'status-red'; // past due
    }
    return 'status-green'; // before reminder
  }

  filterDropdownItems<T extends { id?: string; active?: boolean }>(items: T[], selectedId?: string | null): T[] {
    if (!selectedId) {
      return items.filter(i => i.active !== false);
    }
    return items.filter(i => i.active !== false || i.id === selectedId);
  }

  isNotExpired(expiryDate?: string | Date | null): boolean {
    if (!expiryDate) {
      return false;
    }
    const expiry = expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    if (isNaN(expiry.getTime())) {
      return false;
    }
    return expiry.getTime() >= Date.now();
  }
}
